import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, X, Check } from 'lucide-react';

export default function Criteria() {
  return (
    <div className="page">

      <section style={{ paddingBottom: 64, borderBottom: '1px solid var(--border)' }}>
        <div className="container">
          <div className="red-line" />
          <span className="eyebrow">Who We Work With</span>
          <h1 style={{ fontSize: 'clamp(40px, 5vw, 72px)', maxWidth: 720 }}>
            We're selective.<br />
            <span className="h1-dim">Here's why that helps you.</span>
          </h1>
          <p className="lead" style={{ marginTop: 24 }}>
            We take on a limited number of clients so we can deliver real results for every one of them. We're not the right fit for everyone — and we'd rather tell you that upfront.
          </p>
        </div>
      </section>

      {/* Criteria */}
      <section style={{ borderBottom: '1px solid var(--border)' }}>
        <div className="container">
          <div style={{ marginBottom: 56 }}>
            <div className="red-line" />
            <span className="eyebrow">Our Criteria</span>
            <h2>We work best with companies that meet these standards.</h2>
          </div>

          {[
            { label: 'Business Type', value: 'B2B companies selling to other businesses. We do not work with B2C, e-commerce, or consumer brands.' },
            { label: 'Revenue Stage', value: '$500K+ in annual revenue, or have raised funding. You need to have product-market fit before outbound can scale you.' },
            { label: 'Average Deal Size', value: '$5,000+ ACV. Outbound only makes economic sense when the LTV of a customer justifies the cost of acquisition.' },
            { label: 'Sales Capacity', value: 'You have someone who can take and run meetings. We generate pipeline — you need to be able to close it.' },
            { label: 'Time Commitment', value: 'Minimum 90-day engagement. Outbound systems take time to test, optimize, and compound. We don\'t do one-off campaigns.' },
            { label: 'Communication', value: 'A single point of contact on your team who can give us 2–3 hours per month for reviews, approvals, and strategy.' },
            { label: 'Geography', value: 'We primarily serve Canadian and US-based B2B companies, but have worked with international clients on a case-by-case basis.' }
          ].map((c, i) => (
            <div key={i} className="criteria-item">
              <div className="criteria-label">{c.label}</div>
              <p style={{ fontSize: 15, color: 'var(--gray-light)', lineHeight: 1.8 }}>{c.value}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Good fit / bad fit */}
      <section style={{ borderBottom: '1px solid var(--border)' }}>
        <div className="container">
          <div style={{ marginBottom: 56 }}>
            <span className="eyebrow">Honest Assessment</span>
            <h2>Good fit vs. not a fit.</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <div style={{ background: 'var(--black-card)', border: '1px solid var(--border)', padding: '48px 40px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 32 }}>
                <div style={{ width: 32, height: 32, background: 'rgba(192,57,43,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Check size={16} style={{ color: 'var(--red)' }} />
                </div>
                <h3 style={{ fontSize: 18 }}>Good Fit</h3>
              </div>
              {[
                'B2B SaaS, tech, or services company',
                'Selling to SMB, mid-market, or enterprise',
                'Have a defined sales process',
                'Ready to invest in a system (not a quick fix)',
                'Open to testing, iterating, and learning',
                'Have a product people actually want'
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
                  <Check size={14} style={{ color: 'var(--red)', marginTop: 2, flexShrink: 0 }} />
                  <span style={{ fontSize: 14, color: 'var(--gray-light)' }}>{item}</span>
                </div>
              ))}
            </div>
            <div style={{ background: 'var(--black-card)', border: '1px solid var(--border)', padding: '48px 40px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 32 }}>
                <div style={{ width: 32, height: 32, background: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <X size={16} style={{ color: 'var(--gray-mid)' }} />
                </div>
                <h3 style={{ fontSize: 18 }}>Not a Fit</h3>
              </div>
              {[
                'B2C, e-commerce, or consumer products',
                'Pre-revenue or no product-market fit yet',
                'Looking for a magic bullet in 30 days',
                'Under $5K average deal size',
                'No internal capacity to take meetings',
                'Want to "try it" without committing'
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
                  <X size={14} style={{ color: '#555', marginTop: 2, flexShrink: 0 }} />
                  <span style={{ fontSize: 14, color: 'var(--gray-mid)' }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section style={{ background: 'var(--red)', padding: '80px 0' }}>
        <div className="container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 32 }}>
          <div>
            <h2 style={{ color: 'var(--white)', marginBottom: 12 }}>Think you're a fit?</h2>
            <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 15 }}>Book a call. We'll know in 30 minutes if we can help you.</p>
          </div>
          <Link to="/contact" className="btn-outline" style={{ borderColor: 'rgba(255,255,255,0.4)', color: 'var(--white)', flexShrink: 0 }}>
            Book a Strategy Call <ArrowRight size={14} />
          </Link>
        </div>
      </section>

    </div>
  );
}
